import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { useCards, useNote, useReading, useSaveNote, useSpreads } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { DeckCardImage } from '../components/DeckCardImage.js';
import { PreviewPlaceholder } from '../components/PreviewPlaceholder.js';
import { CardSkeleton, Skeleton } from '../components/Skeleton.js';
import { Spinner } from '../components/Spinner.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';
import { indexById } from '../utils/collections.js';

export const NotesPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPreviewMode } = useStoryblokPreview();

  const { data: readingData, isLoading: readingLoading } = useReading({
    readingId: id ?? '',
    include: ['draws'],
  });
  const { data: noteData, isLoading: noteLoading } = useNote({ readingId: id ?? '' });
  const { data: cardsData } = useCards({ include: ['decks'] });
  const { data: spreadsData } = useSpreads();

  const [content, setContent] = useState('');
  const [initialized, setInitialized] = useState(false);

  // Initialize textarea from existing note once loaded
  useEffect(() => {
    if (noteData?.note && !initialized) {
      setContent(noteData.note.content);
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData?.note]);

  const saveNote = useSaveNote();

  const handleSave = () => {
    if (!content.trim()) {
      return;
    }
    saveNote.mutate(
      { readingId: id ?? '', content },
      {
        onSuccess: () => {
          void navigate(`/readings/${id}`);
        },
      },
    );
  };

  if (readingLoading || noteLoading) {
    return (
      <>
        <div className="flex gap-3 overflow-x-auto py-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[10/14] h-[100px] shrink-0 rounded-lg" />
          ))}
        </div>
        <CardSkeleton lines={5} />
      </>
    );
  }

  const reading = readingData?.reading;
  const draws = readingData?.draws ?? [];

  // Ownership check — only the reading's owner may write notes
  if (!isPreviewMode && reading && user && reading.userId !== user.id) {
    return <Navigate to={`/readings/${id}`} replace />;
  }

  if (isPreviewMode) {
    return <PreviewPlaceholder />;
  }

  const cardMap = indexById(cardsData?.cards ?? []);
  const deckSlug = indexById(cardsData?.decks ?? []).get(reading?.deckId ?? '')?.slug;
  const spread = (spreadsData?.spreads ?? []).find((s) => s.id === reading?.spreadId);

  if (!reading) {
    return (
      <Card>
        <div className="text-center">
          <p className="text-text-secondary">Reading not found</p>
          <Link to="/" className="mt-4 inline-block text-text underline">
            Go home
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-xl font-medium text-text">Notes</h1>
        <p className="mt-1 text-sm text-text-secondary">{spread?.name ?? 'Reading'}</p>
      </div>

      {/* Cards drawn */}
      <div className="flex gap-2 overflow-x-auto py-2">
        {draws.map((draw) => {
          const card = cardMap.get(draw.cardId);
          if (!card) {
            return <></>;
          }
          return (
            <DeckCardImage
              key={card.id}
              className="w-14 shrink-0"
              slug={card.slug}
              deckSlug={deckSlug}
              name={card.name}
            />
          );
        })}
      </div>

      {/* Note textarea */}
      <Card>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your thoughts about this reading..."
          className="min-h-[200px] w-full resize-none border-0 bg-transparent text-text placeholder:text-disabled-text focus:outline-none focus:ring-0"
          autoFocus
        />
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="ghost" onClick={() => void navigate(`/readings/${id}`)} className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saveNote.isPending || !content.trim()}
          className="flex-1"
        >
          {saveNote.isPending ? <Spinner size="sm" className="text-white" /> : 'Save'}
        </Button>
      </div>
    </>
  );
};
